import clsx from 'clsx'
import {useFormik} from 'formik'
import {get, isEmpty, isUndefined, omit, size} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {ConfirmActionModal, KTSVG} from '../../../../../gori/components'
import {formatBytes} from '../../../../../gori/helpers'
import {
  useCancelToken,
  UsePermission,
  UseUploadFile,
  UseYupValidate,
} from '../../../../../gori/hooks'
import {useA1BatchesProvider, useDisabled} from '../../../../../gori/providers'
import {
  Button,
  InputTextFormik,
  UploadFile,
  ValidationErrorModal,
} from '../../../../../gori/widgets'
import {A1_CONSTANTS, A1BatchService, T86DocumentTable} from '../../../a1'
import {FlightInfoCard} from './FlightInfoCard'
import {FlightInfo, MAWB_REGEX} from '../../definitions'
import {FormError} from '../../../../../gori/widgets/form/FormError'
import {ErrorSection} from '../../components/error'
import {collectGroupErrors} from '../../utils'

type Props = {
  show: boolean
  handleClose: () => void
  handleSuccess: () => void
}

const JourneyInformationModal: React.FC<Props> = ({show, handleClose, handleSuccess}) => {
  const intl = useIntl()
  const {id} = useParams()
  const {setPageDisabled} = useDisabled()
  const {isOrigin, isDestination} = UsePermission()
  const {stringYup, numberYup, regexYup} = UseYupValidate()
  const {isCancel, newCancelToken} = useCancelToken()
  const {a1Batch} = useA1BatchesProvider()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [errorMessages, setErrorMessages] = useState({})
  const [editAdditionalCharge, setEditAdditionalCharge] = useState<{
    show: boolean
    type: 'add' | 'edit' | null
    id: number | null
  }>({
    show: false,
    type: null,
    id: null,
  })

  const [existsDocument, setExistsDocument] = useState<boolean>(false)
  const [loading, setLoading] = useState<{update: boolean; download: boolean; flightInfo: boolean}>(
    {
      update: false,
      download: false,
      flightInfo: false,
    }
  )
  const miscCurrent = useRef(0)
  const errorSectionRef = useRef<HTMLDivElement | null>(null)
  const [showModal, setShowModal] = useState<{confirmAdditionalCharge: boolean}>({
    confirmAdditionalCharge: false,
  })
  const [flights, setFlights] = useState<FlightInfo[] | undefined>(
    a1Batch?.journey_flight_infos || undefined
  )
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const {setFile, handleDelete} = UseUploadFile({
    fileInput: [],
    multiple: false,
    supportedFormat: [A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS.CSV],
    maxRow: A1_CONSTANTS.UPLOAD_CONFIG.MAX_UPLOAD,
    onSubmit: (files) => {
      formik.setFieldValue('file', files[0] || null)
    },
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const setFieldError = (field: string, message: string) => {
    setErrors((prev) => ({...prev, [field]: message}))
  }

  const handleCloseModal = () => {
    handleClose()
  }

  const miscValidation = useMemo(() => {
    return Yup.object().shape({
      title: stringYup(255, 'CHARGE_NAME'),
      amount: numberYup.journey_info({
        name: 'AMOUNT',
        required: false,
        min: {value: 0, label: '0'},
        max: {value: 1000000, label: '1,000,000'},
      }),
    })
  }, [numberYup, stringYup])

  const initValidateSchema = useMemo(() => {
    return {
      edit_misc: Yup.object(),
      mawb: regexYup.mawb('MAWB', true),
      shipment_detail: Yup.object().shape({
        pallets: numberYup.journey_info({
          name: 'PALLETS',
          integer: true,
          max: {value: 10000, label: '10,000'},
        }),
        parcels: numberYup.journey_info({
          name: 'PARCELS',
          integer: true,
          max: {value: 10000, label: '10,000'},
        }),
        weight: numberYup.journey_info({
          name: 'KGS',
          min: {value: 0, label: '0'},
          max: {value: 1000000, label: '1,000,000'},
          moreThan: true,
        }),
      }),
      charges: Yup.object().shape(
        ['PICK_UP', 'CONSOLIDATION', 'FREIGHT', 'CLEARANCE', 'DECONSOLIDATION', 'TRANSFER'].reduce(
          (acc, name) => {
            acc[name.toLowerCase()] = numberYup.journey_info({
              name,
              required: false,
              min: {value: 0, label: '0'},
              max: {value: 1000000, label: '1,000,000'},
            })
            return acc
          },
          {} as Record<string, any>
        )
      ),
      t86_files: Yup.array().nullable(),
    }
  }, [numberYup, regexYup])

  const initialValues = useMemo(() => {
    return {
      misc: {},
      edit_misc: {title: null, amount: null},
      mawb: a1Batch?.mawb || null,
      shipment_detail: {
        pallets: a1Batch?.pallets || null,
        parcels: a1Batch?.parcels || null,
        weight: a1Batch?.weight || null,
      },
      charges: {
        pick_up: a1Batch?.origin_pick_up || null,
        consolidation: a1Batch?.origin_consolidation || null,
        freight: a1Batch?.origin_freight || null,
        clearance: a1Batch?.destination_clearance || null,
        deconsolidation: a1Batch?.destination_deconsolidation || null,
        transfer: a1Batch?.destination_transfer || null,
      },
      file: null as File | null,
      t86_files: [],
    }
  }, [a1Batch])

  const [validateSchema, setValidateSchema] = useState<any>(initValidateSchema)

  const handleUpdateJourneyInfo = useCallback(
    async ({values, hasPass = false}) => {
      if (!hasPass && editAdditionalCharge.show) {
        return setShowModal((prev) => ({...prev, confirmAdditionalCharge: true}))
      }

      try {
        setErrorMessages({})
        setLoading((prev) => ({...prev, update: true}))
        const config = {cancelToken: newCancelToken()}
        const _payload = {
          ...omit(
            values,
            'origin',
            'destination',
            'invalid_date',
            'layover',
            'file',
            'misc',
            'edit_misc'
          ),
          charges: {
            ...values.charges,
            [`${isOrigin ? 'origin' : 'destination'}_misc`]: Object.values(values.misc),
          },
        }

        const payload = new FormData()
        payload.append('data', JSON.stringify(_payload))
        if (values?.file) {
          payload.append('file', values.file)
        }
        if (values?.t86_files) {
          const t86_files = values?.t86_files
          t86_files.map((file) => {
            payload.append('t86_files[]', file)
            return file
          })
        }
        const res: any = await A1BatchService.updateJourneyInfo(a1Batch.id, payload, config)
        if (res) {
          toast.success(res.message)
          handleSuccess()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setErrorMessages(error?.response?.data)
      } finally {
        setLoading((prev) => ({...prev, update: false}))
      }
    },
    [a1Batch.id, editAdditionalCharge.show, handleSuccess, isCancel, isOrigin, newCancelToken]
  )
  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape(validateSchema),
    onSubmit: (values) => {
      handleUpdateJourneyInfo({values})
    },
  })

  const handleDownloadTemplate = async () => {
    try {
      setLoading((prev) => ({...prev, download: true}))
      const response: any = await A1BatchService.downloadOrdersA1Batch(a1Batch.id, {
        cancelToken: newCancelToken(),
      })
      if (response) {
        const blob = new Blob([response.content], {type: response.type})
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = response.file_name
        link.click()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, download: false}))
    }
  }

  useEffect(() => {
    const handleInitValueMisc = async () => {
      let miscValues = {}
      a1Batch?.[isOrigin ? 'origin_misc' : 'destination_misc']?.forEach((misc) => {
        miscValues[miscCurrent.current] = {
          title: misc.title,
          amount: misc.amount,
        }
        miscCurrent.current++
      })

      await formik.setFieldValue('misc', miscValues)
    }

    if (!isEmpty(a1Batch?.origin_misc) || !isEmpty(a1Batch?.destination_misc)) {
      handleInitValueMisc()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.initialValues])

  useEffect(() => {
    setFlights(a1Batch?.journey_flight_infos || undefined)
  }, [a1Batch?.journey_flight_infos])

  useEffect(() => {
    if (!isEmpty(errorMessages)) {
      errorSectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [errorMessages])

  const checkExistDocument = useCallback(async () => {
    try {
      setPageDisabled(true)
      const config = {
        cancelToken: newCancelToken(),
      }
      const response: any = await A1BatchService.checkExistsDocument(id, config)
      if (response) {
        setExistsDocument(response.exists ?? false)
        // Yamato batches do not require document upload
        if (!response.exists && !a1Batch?.is_yamato) {
          setValidateSchema((prev) => ({
            ...prev,
            t86_files: Yup.array().min(1, intl.formatMessage({id: 'REQUIRED'})),
          }))
        }
      }
    } catch (error: any) {
      if (isCancel(error)) return
    } finally {
      setPageDisabled(false)
    }
  }, [id, isCancel, newCancelToken, setPageDisabled, setValidateSchema, intl, a1Batch?.is_yamato])

  useEffect(() => {
    if (isOrigin) {
      checkExistDocument()
    }
  }, [checkExistDocument, isOrigin])

  const handleAdditionalCharge = ({type, id}) => {
    setEditAdditionalCharge({show: true, type: type, id: id})
    setValidateSchema((prev) => ({
      ...prev,
      edit_misc: miscValidation,
    }))
  }

  const getFlightInfo = async (mawb: string) => {
    if (!mawb || formik.errors.mawb || !MAWB_REGEX.test(mawb)) {
      setFlights(undefined)

      return
    }
    if (errors.mawb) {
      setFieldError('mawb', '')
    }
    try {
      setLoading((prev) => ({...prev, flightInfo: true}))
      const response: any = await A1BatchService.getBatchFlightInfo(a1Batch.id, mawb)
      setFlights(response?.data || [])
    } catch (error: any) {
      setFlights(undefined)
      if (isCancel(error)) return
      setFieldError(
        'mawb',
        error?.response?.data?.message || intl.formatMessage({id: 'INVALID_MAWB_FORMAT'})
      )
    } finally {
      setLoading((prev) => ({...prev, flightInfo: false}))
    }
  }

  const handleCancelEditAdditionalCharge = useCallback(() => {
    formik.setFieldValue('edit_misc', {title: null, amount: null})
    formik.setTouched({...formik.touched, edit_misc: {title: false, amount: false}})
    setValidateSchema((prev) => omit(prev, 'edit_misc'))
    setEditAdditionalCharge({show: false, type: null, id: null})
  }, [formik])

  const handleSaveEditAdditionalCharge = useCallback(() => {
    if (!formik.values?.edit_misc.title || formik.errors.edit_misc) {
      return formik.setTouched({...formik.touched, edit_misc: {title: true, amount: true}})
    }

    miscCurrent.current = Number(miscCurrent.current) + 1
    formik.setFieldValue(`misc.${editAdditionalCharge?.id}`, formik.values?.edit_misc)

    handleCancelEditAdditionalCharge()
  }, [editAdditionalCharge?.id, formik, handleCancelEditAdditionalCharge])

  const handleDeleteEditAdditionalCharge = () => {
    const misc = formik.values?.misc
    if (editAdditionalCharge?.id !== null && misc[editAdditionalCharge?.id]) {
      delete misc?.[editAdditionalCharge?.id]
      formik.setValues((prev) => ({
        ...prev,
        misc: misc,
      }))
    }

    handleCancelEditAdditionalCharge()
  }

  const handleCloseConfirmAdditionalCharge = useCallback(() => {
    setShowModal((prev) => ({...prev, confirmAdditionalCharge: false}))
  }, [])

  const handleConfirmSaveAdditionalCharge = useCallback(() => {
    if (!editAdditionalCharge.id?.toString()) return

    const values = {
      ...formik.values,
      misc: {...formik.values.misc, [editAdditionalCharge?.id]: formik.values?.edit_misc},
    }
    handleCloseConfirmAdditionalCharge()
    handleUpdateJourneyInfo({values, hasPass: true})
  }, [
    editAdditionalCharge.id,
    formik.values,
    handleCloseConfirmAdditionalCharge,
    handleUpdateJourneyInfo,
  ])

  const handleConfirmSkipAdditionalCharge = useCallback(() => {
    handleCloseConfirmAdditionalCharge()
    handleUpdateJourneyInfo({values: formik.values, hasPass: true})
  }, [formik.values, handleCloseConfirmAdditionalCharge, handleUpdateJourneyInfo])

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      {showModal.confirmAdditionalCharge && (
        <ConfirmActionModal
          show={showModal.confirmAdditionalCharge}
          classNameDialog='mw-500px'
          title={intl.formatMessage({id: 'ADDITIONAL_CHARGE_CONFIRMATION'})}
          message={
            <span className='fw-bold text-center'>
              <p>{intl.formatMessage({id: 'THE_ADDITIONAL_CHARGE_HAS_NOT_BEEN_SAVED'})}</p>
              <p>{intl.formatMessage({id: 'WOULD_YOU_LIKE_TO_SAVE_IT_BEFORE_PROCEEDING'})}</p>
            </span>
          }
          messagePrimary={intl.formatMessage({id: 'YES'})}
          messageSecondary={intl.formatMessage({id: 'NO'})}
          handlePrimaryAction={handleConfirmSaveAdditionalCharge}
          handleSecondaryAction={handleConfirmSkipAdditionalCharge}
          handleClose={handleCloseConfirmAdditionalCharge}
        />
      )}
      <Modal
        id='gori_modal_journey_information_modal'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-1250px h-auto'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'JOURNEY_INFORMATION'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='vh-75 scroll-y'>
            <Container>
              <div ref={errorSectionRef} />
              {isEmpty(get(errorMessages, 'errors')) && (
                <ErrorSection message={get(errorMessages, 'message')} className='mb-4' />
              )}
              <div className='border-bottom'>
                <h3 className='required'>{intl.formatMessage({id: 'FLIGHT_INFORMATION'})}</h3>
                <div className='d-flex mt-4 mb-6'>
                  <div className='flex-fill'>
                    <div
                      className={clsx('col-12 col-md-4 d-flex position-relative', {
                        'mb-4': !errors.mawb,
                      })}
                    >
                      {isOrigin ? (
                        <InputTextFormik
                          className='col-7'
                          label={'MAWB'}
                          labelClassName='col-5 col-form-label fs-5 fw-bold'
                          formik={formik}
                          name='mawb'
                          disabled={isDestination}
                          inputClassName={clsx({
                            'border-danger': !!errors.mawb,
                          })}
                          onChange={() => {
                            if (errors.mawb) {
                              setFieldError('mawb', '')
                            }
                          }}
                          onBlurCallback={(e) => {
                            getFlightInfo(e.target.value)
                          }}
                        />
                      ) : (
                        <div className='fs-6 fw-bolder my-1'>
                          {intl.formatMessage({id: 'MAWB'})}: {formik.values.mawb}
                        </div>
                      )}

                      {formik.values.mawb && !isDestination && !formik.errors.mawb && (
                        <Button
                          disabled={isDestination}
                          event={() => {
                            formik.setFieldValue('mawb', '')
                            setFlights(undefined)
                          }}
                          divClassName='position-absolute h-100 end-0 d-flex justify-content-center align-items-center'
                          className={'text-hover-dark border-0 bg-white p-3 me-1'}
                        >
                          {loading.flightInfo ? (
                            <KTSVG
                              path='/media/gori/a1/sync.svg'
                              className={clsx('svg-icon-3 spin_reverse', {})}
                            />
                          ) : (
                            <KTSVG
                              path='/media/gori/common/delete.svg'
                              className='m-0'
                              svgClassName='mh-10px'
                            />
                          )}
                        </Button>
                      )}
                    </div>
                    {errors.mawb && !formik.errors?.mawb && (
                      <div className='col-12 col-md-4 row mb-4'>
                        <div className='col-5' />
                        <FormError message={errors.mawb} className='col-7 ' />
                      </div>
                    )}

                    <div className='row g-4'>
                      {(flights || []).map((flight, index) => (
                        <div key={flight.id} className='col-12'>
                          <FlightInfoCard
                            flight={flight}
                            isLayover={
                              !isUndefined(flights) &&
                              flights?.length > 2 &&
                              index !== 0 &&
                              index !== flights?.length - 1
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <ErrorSection message={get(errorMessages, 'errors.mawb')} />
              </div>
              {isOrigin && (
                <>
                  <div className='border-bottom mt-10'>
                    <h3 className='required'>
                      {intl.formatMessage({id: 'LAST_MILE_SHIPMENT_DETAIL'})}
                    </h3>
                    <div className='d-flex flex-column flex-md-row my-8'>
                      <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                        <InputTextFormik
                          className='col-7'
                          label={intl.formatMessage({id: 'PALLETS'})}
                          labelClassName='col-5 col-form-label fs-5 fw-bold'
                          formik={formik}
                          type='number'
                          isInteger
                          name='shipment_detail.pallets'
                        />
                      </div>
                      <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                        <InputTextFormik
                          className='col-7'
                          label={intl.formatMessage({id: 'PARCELS'})}
                          labelClassName='col-5 col-form-label fs-5 fw-bold ps-0 ps-md-3'
                          formik={formik}
                          type='number'
                          isInteger
                          name='shipment_detail.parcels'
                        />
                      </div>
                      <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                        <InputTextFormik
                          className='col-7'
                          label={intl.formatMessage({id: 'KGS'})}
                          labelClassName='col-5 col-form-label fs-5 fw-bold ps-0 ps-md-3'
                          formik={formik}
                          type='number'
                          name='shipment_detail.weight'
                        />
                      </div>
                    </div>
                    <ErrorSection
                      message={collectGroupErrors(
                        get(errorMessages, 'errors'),
                        'data.shipment_detail'
                      )}
                    />
                  </div>
                  <div className='mt-10'>
                    <h3>{intl.formatMessage({id: 'CHARGES'})}</h3>
                    <div className='d-flex flex-column flex-md-row mt-8'>
                      <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                        <InputTextFormik
                          className='col-7'
                          type='number'
                          label={intl.formatMessage({id: 'PICK_UP'})}
                          labelClassName='col-5 col-form-label fs-5 fw-bold'
                          formik={formik}
                          name='charges.pick_up'
                          configAppend={{
                            name: 'USD',
                            position: 'right',
                            classInput: 'pe-14',
                          }}
                        />
                      </div>
                      <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                        <InputTextFormik
                          className='col-7'
                          type='number'
                          label={intl.formatMessage({id: 'CONSOLIDATION'})}
                          labelClassName='col-5 col-form-label fs-5 fw-bold ps-0 ps-md-3'
                          formik={formik}
                          name='charges.consolidation'
                          configAppend={{
                            name: 'USD',
                            position: 'right',
                            classInput: 'pe-14',
                          }}
                        />
                      </div>
                      <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                        <InputTextFormik
                          className='col-7'
                          type='number'
                          label={intl.formatMessage({id: 'FREIGHT'})}
                          labelClassName='col-5 col-form-label fs-5 fw-bold ps-0 ps-md-3'
                          formik={formik}
                          name='charges.freight'
                          configAppend={{
                            name: 'USD',
                            position: 'right',
                            classInput: 'pe-14',
                          }}
                        />
                      </div>
                    </div>
                    <ErrorSection
                      message={collectGroupErrors(get(errorMessages, 'errors'), 'data.charges')}
                    />
                  </div>
                </>
              )}
              {isDestination && (
                <div className='mt-10'>
                  <h3>{intl.formatMessage({id: 'CHARGES'})}</h3>
                  <div className='d-flex flex-column flex-md-row mt-8'>
                    <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                      <InputTextFormik
                        className='col-7'
                        type='number'
                        label={intl.formatMessage({id: 'CLEARANCE'})}
                        labelClassName='col-5 col-form-label fs-5 fw-bold'
                        formik={formik}
                        name='charges.clearance'
                        configAppend={{
                          name: 'USD',
                          position: 'right',
                          classInput: 'pe-25',
                        }}
                      />
                    </div>
                    <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                      <InputTextFormik
                        className='col-7'
                        type='number'
                        label={intl.formatMessage({id: 'DECONSOLIDATION'})}
                        labelClassName='col-5 col-form-label fs-5 fw-bold ps-0 ps-md-3'
                        formik={formik}
                        name='charges.deconsolidation'
                        configAppend={{
                          name: 'USD',
                          position: 'right',
                          classInput: 'pe-25',
                        }}
                      />
                    </div>
                    <div className='d-flex col-12 col-md-4 mt-4 mt-md-0'>
                      <InputTextFormik
                        className='col-7'
                        type='number'
                        label={intl.formatMessage({id: 'TRANSFER'})}
                        labelClassName='col-5 col-form-label fs-5 fw-bold ps-0 ps-md-3'
                        formik={formik}
                        name='charges.transfer'
                        configAppend={{
                          name: 'USD',
                          position: 'right',
                          classInput: 'pe-25',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className='border-bottom'>
                <div className='d-flex flex-column flex-md-row flex-wrap'>
                  {formik.values?.misc &&
                    Object.entries(formik.values?.misc).map(([key, value]: [string, any], idx) => {
                      return (
                        <div
                          className='d-flex col-12 col-md-4 mt-4 cursor-pointer'
                          key={idx}
                          onClick={() => {
                            formik.setFieldValue('edit_misc', value)
                            handleAdditionalCharge({type: 'edit', id: Number(key)})
                          }}
                        >
                          <InputTextFormik
                            className='col-7 pe-none'
                            type='number'
                            label={value.title}
                            labelClassName={clsx(
                              'col-5 col-form-label fs-5 fw-bold text-truncate cursor-pointer',
                              {'px-0 px-md-3': idx % 3 !== 0}
                            )}
                            formik={formik}
                            name={`misc.${key}.amount`}
                            configAppend={{
                              name: 'USD',
                              position: 'right',
                              classInput: 'pe-25',
                            }}
                          />
                        </div>
                      )
                    })}
                </div>
                {editAdditionalCharge?.show && editAdditionalCharge?.id !== null ? (
                  <div className='bg-light border-bottom my-4'>
                    <div className='d-flex justify-content-between align-items-center bg-secondary py-4 px-2 '>
                      <h3 className='m-0 mw-500px text-truncate'>
                        {editAdditionalCharge?.type === 'add'
                          ? intl.formatMessage({id: 'ADD_ADDITIONAL_CHARGE'})
                          : formik.values?.misc?.[editAdditionalCharge?.id]?.title}
                      </h3>
                      <div className='d-flex align-items-center gap-4'>
                        <span className='cursor-pointer' onClick={handleCancelEditAdditionalCharge}>
                          <KTSVG
                            path='/media/gori/common/delete.svg'
                            className='svg-icon-3 text-danger'
                          />
                        </span>
                        <span className='cursor-pointer' onClick={handleSaveEditAdditionalCharge}>
                          <KTSVG path='/media/gori/common/valid.svg' className='svg-icon-1' />
                        </span>
                        {editAdditionalCharge?.type === 'edit' && (
                          <span
                            className='cursor-pointer'
                            onClick={handleDeleteEditAdditionalCharge}
                          >
                            <KTSVG path='/media/gori/common/empty.svg' className='svg-icon-1' />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='d-flex flex-column flex-md-row justify-content-between p-4 gap-6'>
                      <div className='d-flex col-12 col-md-5'>
                        <InputTextFormik
                          className='col-8'
                          label={intl.formatMessage({id: 'CHARGE_NAME'})}
                          labelClassName='col-4 col-form-label fs-5 fw-bold'
                          formik={formik}
                          name='edit_misc.title'
                        />
                      </div>
                      <div className='d-flex col-12 col-md-5'>
                        <InputTextFormik
                          className='col-8'
                          type='number'
                          label={intl.formatMessage({id: 'AMOUNT'})}
                          labelClassName='col-4 col-form-label fs-5 fw-bold'
                          formik={formik}
                          name='edit_misc.amount'
                          configAppend={{
                            name: 'USD',
                            position: 'right',
                            classInput: 'pe-25',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={clsx(
                      'cursor-pointer fw-bold text-gray-700 my-4 py-4 px-2 bg-light border-secondary border-top border-bottom',
                      {
                        'cursor-no-drop opacity-75':
                          size(formik.values?.misc) >= A1_CONSTANTS.MAX_ADDITIONAL_CHARGE,
                        'bg-hover-primary-light text-hover-primary':
                          size(formik.values?.misc) < A1_CONSTANTS.MAX_ADDITIONAL_CHARGE,
                      }
                    )}
                    onClick={() => {
                      if (size(formik.values?.misc) < A1_CONSTANTS.MAX_ADDITIONAL_CHARGE) {
                        handleAdditionalCharge({type: 'add', id: miscCurrent.current})
                      }
                    }}
                  >
                    + {intl.formatMessage({id: 'ADD_ADDITIONAL_CHARGE'})}
                  </div>
                )}
              </div>

              {isOrigin && (
                <>
                  <div className='mt-10'>
                    <div className='d-flex align-items-center justify-content-between'>
                      <h3
                        className={clsx({
                          required: isOrigin && !existsDocument && !a1Batch?.is_yamato,
                        })}
                      >
                        {intl.formatMessage({id: 'DOCUMENTS'})}
                      </h3>
                    </div>
                    <ErrorSection
                      className='mt-4'
                      message={get(errorMessages, 'errors.t86_files')}
                    />

                    <UploadFile
                      containerClassName='px-8 py-6 shadow-sm rounded-4 bg-white my-4'
                      className='border-primary border-2'
                      onSubmit={(e) => formik.setFieldValue('t86_files', e.files)}
                      supportedFormat={Object.values(A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS)}
                      size={{label: '8MB', value: A1_CONSTANTS.UPLOAD_CONFIG.SIZE}}
                      multiple={true}
                    />
                    {formik.errors.t86_files && formik.touched.t86_files && (
                      <div className='text-danger mt-2'>{formik.errors.t86_files}</div>
                    )}
                  </div>
                  {existsDocument && <T86DocumentTable />}
                  <div className='mt-10'>
                    <div className='d-flex align-items-center justify-content-between'>
                      <div className='d-flex gap-8'>
                        <h3 className='m-0'>{intl.formatMessage({id: 'UPDATE_CONTAINER_ID'})}</h3>
                        <span
                          className='text-gray-700 text-hover-dark text-decoration-underline cursor-pointer'
                          onClick={handleDownloadTemplate}
                        >
                          {intl.formatMessage({id: 'DOWNLOAD_TEMPLATE'})}
                        </span>
                        <span
                          className='text-teal text-decoration-underline cursor-pointer'
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {intl.formatMessage({id: 'UPLOAD_FILE'})}
                        </span>
                        <input
                          type='file'
                          ref={fileInputRef}
                          onChange={setFile}
                          className='d-none'
                          accept={A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS.CSV.value}
                        />
                      </div>
                      {formik.values?.file && (
                        <div className='d-flex align-items-center mt-2 col-6'>
                          <div className='flex-fill d-flex justify-content-between bg-teal-light p-3 rounded-2 fw-bold w-75'>
                            <span className='text-truncate'>{formik.values?.file?.name}</span>
                            <span className='text-nowrap'>
                              {formatBytes(formik.values?.file?.size)}
                            </span>
                          </div>
                          <div
                            className='text-hover-danger cursor-pointer ms-4 bg-teal-light d-flex w-38px h-38px justify-content-center align-items-center rounded-2'
                            onClick={() => handleDelete(0)}
                          >
                            <KTSVG path='/media/gori/common/empty.svg' svgClassName='mh-36px' />
                          </div>
                        </div>
                      )}
                    </div>
                    <ErrorSection className='mt-4' message={get(errorMessages, 'errors.file')} />
                  </div>
                </>
              )}
              {isDestination && (
                <div className='mt-10'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <h3>{intl.formatMessage({id: 'UPDATE_CUSTOM'})}</h3>
                    <Button
                      event={handleDownloadTemplate}
                      className='btn btn-sm btn-secondary fw-bolder'
                      label={intl.formatMessage({id: 'EXPORT_AS_CSV'})}
                      children={<KTSVG path='/media/gori/a1/fileDownload.svg' />}
                      loading={loading.download}
                    />
                  </div>
                  <ErrorSection className='mt-4' message={get(errorMessages, 'errors.file')} />
                  <UploadFile
                    containerClassName='px-8 py-6 shadow-sm rounded-4 bg-white my-4'
                    className='bg-light-primary border-primary border-2'
                    onSubmit={(e) => formik.setFieldValue('file', e.files?.[0] || null)}
                    supportedFormat={[A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS.CSV]}
                    maxRow={A1_CONSTANTS.UPLOAD_CONFIG.MAX_UPLOAD}
                    size={{label: '8MB', value: A1_CONSTANTS.UPLOAD_CONFIG.SIZE}}
                  />
                </div>
              )}
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-secondary me-2'
                label={intl.formatMessage({id: 'CANCEL'})}
                event={handleCloseModal}
              />
              <Button
                className='btn btn-primary'
                label={intl.formatMessage({id: 'UPDATE'})}
                disabled={loading.update || isUndefined(flights) || !!errors.mawb}
                event={formik.handleSubmit}
                loading={loading.update}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {JourneyInformationModal}
